import React, { useEffect, useMemo, useState, useRef, memo, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { Global } from "../../stitches.config";
import { useStores } from "../../helpers";

import Adslot from "../../components/Adslot";
import { ArticleWrapper, ArticleRow, ArticleColumn } from "../../components/ArticleNormalBizon";
import Content from "../../components/Article/Content";

type ArticleBizonQuizPageProps = {
    quizId: string;
    quizQuestion: string;
};

const ArticleBizonQuizPage: React.FC<ArticleBizonQuizPageProps> = memo(({ quizId, quizQuestion }) => {

    const { ArticleStore } = useStores();

    const navigate = useNavigate();

    //Controll quiz component from page (parent)
    const [quizCurrentQuestion, setQuizCurrentQuestion] = useState<number>(0);
    const quizQuestionRef = useRef<number>(0); //Ref to pass in GTAG
    const goToNextQuestionFallback = useRef<NodeJS.Timeout | null>(null);

    //Set currentQuestionId and update location
    const setQuestion = useCallback((questionId: number) => {
        setQuizCurrentQuestion(questionId);

        navigate({
            pathname: `${quizQuestion ? '..' : '.'}/${questionId ? questionId : ''}`
        }, {
            state: {
                soft: true
            },
            relative: 'path'
        });

        //Stop fallback function
        goToNextQuestionFallback.current && clearTimeout(goToNextQuestionFallback.current);
    }, [quizQuestion]);

    const QuizPart = useMemo(() => ArticleStore?.article?.BodyPart?.find((p: any) => p.type == "quiz2" && p.quiz_id == quizId), [quizId, ArticleStore.article]);

    //Handle click answer (refresh adslot - then wait and setQuestion)
    const handleGoToNextQuestion = useCallback((questionId: number) => {
        window.dispatchEvent(new CustomEvent(`prebid_ART_Billboard_1_refresh`));
        goToNextQuestionFallback.current = setTimeout(() => setQuestion(questionId), 3000);
        quizQuestionRef.current = questionId;
    }, [QuizPart, setQuestion]);

    const QuizArticle = useMemo(() => (QuizPart ? {
        ...ArticleStore?.article,
        MiddleParts: [],
        BodyPart: [{
            ...QuizPart,
            quiz_type: 'page',
            quiz_question: quizCurrentQuestion,
            handleGoToNextQuestion
        }]
    } : {}), [QuizPart, quizCurrentQuestion, handleGoToNextQuestion]);

    //Handle adSlot refresh and setQuestion
    useEffect(() => {
        const { googletag } = window;

        const handleSlotRenderEnd = (event: any) => {
            if (event.slot.getSlotElementId() === "ART_Billboard_1") {
                //Get current route question id from ref - we do not have to attach gtag event listener multiple times (maybe caused some errors)
                setQuestion(quizQuestionRef.current);
            }
        };

        googletag.cmd.push(function () {
            googletag.pubads().addEventListener('slotRenderEnded', handleSlotRenderEnd);
        });

        return () => {
            googletag.cmd.push(function () {
                googletag.pubads().removeEventListener('slotRenderEnded', handleSlotRenderEnd);
            });
        }
    }, []);


    useEffect(() => {
        //Back on article when quiz does not exist
        if (!QuizPart)
            navigate(`/${ArticleStore.currentSlug}`);

        const quizQuestionNumber = Number(quizQuestion ?? "0");

        //When user manually enter a url - redirect to quiz start
        if (quizQuestionNumber - quizQuestionRef.current > 1)
            navigate(`/${ArticleStore.currentSlug}/quiz/${QuizPart.quiz_id}`);

    }, [QuizPart, ArticleStore, quizQuestion]);

    if (!QuizPart)
        return null;

    return (
        <>
            <Global
                styles={{
                    ".app-content": {
                        paddingTop: "1px",
                        backgroundColor: "rgb(19, 18, 18)"
                    }
                }}
            />
            <Adslot name="ART_Billboard_1" />
            <ArticleWrapper isSemi>
                <div className="container">
                    <ArticleRow className="row" isSemi>
                        <ArticleColumn className={"col-lg-12 column-left"}>
                            <Content article={QuizArticle} />
                        </ArticleColumn>
                    </ArticleRow>
                </div>
            </ArticleWrapper>
        </>
    );

});

export default ArticleBizonQuizPage;